import { useState, useEffect, useRef } from 'react';
import { X, Send, Users, User, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../ui/Toaster';
import { useDialogA11y } from '../ui/useDialogA11y';

interface TodoTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Course {
  id: string;
  name: string;
}

interface Student {
  id: string;
  full_name: string;
  email: string;
}

export function TodoTaskModal({ isOpen, onClose, onSuccess }: TodoTaskModalProps) {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    courseId: '',
    targetType: 'all',
    selectedStudents: [] as string[],
    dueDate: '',
  });
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && profile?.id) {
      loadCourses();
    }
  }, [isOpen, profile?.id]);

  useEffect(() => {
    if (formData.courseId) {
      loadStudents(formData.courseId);
    }
  }, [formData.courseId]);

  const loadCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('id, name')
        .or(`docente_id.eq.${profile!.id},secondary_docente_id.eq.${profile!.id}`)
        .order('name');

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const loadStudents = async (courseId: string) => {
    try {
      const { data, error } = await supabase
        .from('course_enrollments')
        .select('profiles(id, full_name, email)')
        .eq('course_id', courseId)
        .eq('status', 'active');

      if (error) throw error;

      const studentsList = data
        ?.map((e: any) => e.profiles)
        .filter(Boolean) || [];

      setStudents(studentsList);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const handleStudentToggle = (studentId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedStudents: prev.selectedStudents.includes(studentId)
        ? prev.selectedStudents.filter(id => id !== studentId)
        : [...prev.selectedStudents, studentId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.courseId) {
      toast.error('Completa el título y selecciona un curso');
      return;
    }

    if (formData.targetType === 'individual' && formData.selectedStudents.length === 0) {
      toast.error('Selecciona al menos un estudiante');
      return;
    }

    if (formData.targetType === 'group' && formData.selectedStudents.length === 0) {
      toast.error('Selecciona al menos un estudiante para el grupo');
      return;
    }

    if (formData.targetType === 'all' && students.length === 0) {
      toast.error('No hay estudiantes inscritos en este curso. Inscribe estudiantes primero.');
      return;
    }

    setLoading(true);

    try {
      const { data: task, error: taskError } = await supabase
        .from('todo_tasks')
        .insert({
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          created_by: profile!.id,
          course_id: formData.courseId,
          target_type: formData.targetType,
          due_date: formData.dueDate || null,
        })
        .select()
        .single();

      if (taskError) {
        console.error('Error creating task:', taskError);
        throw new Error(`Error al crear la tarea: ${taskError.message}`);
      }

      let recipientIds: string[] = [];

      if (formData.targetType === 'all') {
        recipientIds = students.map(s => s.id);
      } else if (formData.targetType === 'individual' || formData.targetType === 'group') {
        recipientIds = formData.selectedStudents;
      }

      if (recipientIds.length > 0) {
        const recipients = recipientIds.map(studentId => ({
          task_id: task.id,
          student_id: studentId,
        }));

        const { error: recipientsError } = await supabase
          .from('todo_task_recipients')
          .insert(recipients);

        if (recipientsError) {
          console.error('Error creating recipients:', recipientsError);
          await supabase.from('todo_tasks').delete().eq('id', task.id);
          throw new Error(`Error al asignar destinatarios: ${recipientsError.message}`);
        }
      }

      toast.success('Tarea creada y enviada');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error in handleSubmit:', error);
      toast.error(error.message || 'Error al crear la tarea. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      courseId: '',
      targetType: 'all',
      selectedStudents: [],
      dueDate: '',
    });
    setStudents([]);
    onClose();
  };

  useDialogA11y(isOpen, handleClose, dialogRef);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-todotask"
        tabIndex={-1}
        className="glass-deep rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/[0.06]"
      >
        <div className="flex justify-between items-center p-6 border-b border-white/[0.06]">
          <h2 id="modal-title-todotask" className="text-display text-2xl font-bold text-white">Crear Tarea TO-DO</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Título de la Tarea *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 glass-surface border border-white/[0.08] rounded-lg text-white focus:ring-2 focus:ring-cubus-lime focus:border-transparent"
                placeholder="Ej: Completar ejercicios del módulo 3"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Descripción (opcional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 glass-surface border border-white/[0.08] rounded-lg text-white focus:ring-2 focus:ring-cubus-lime focus:border-transparent resize-none"
                placeholder="Describe los detalles de la tarea..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Curso *
              </label>
              <select
                value={formData.courseId}
                onChange={(e) => setFormData({ ...formData, courseId: e.target.value, selectedStudents: [] })}
                className="w-full px-4 py-2 border border-white/[0.08] rounded-lg text-white focus:ring-2 focus:ring-cubus-lime focus:border-transparent focus:outline-none"
                style={{ backgroundColor: '#1a1a1a' }}
                required
              >
                <option value="" style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>Selecciona un curso</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id} style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>
                    {course.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Fecha de Vencimiento (opcional)
              </label>
              <input
                type="datetime-local"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                className="w-full px-4 py-2 glass-surface border border-white/[0.08] rounded-lg text-white focus:ring-2 focus:ring-cubus-lime focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-3">
                Destinatarios *
              </label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, targetType: 'all', selectedStudents: [] })}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                    formData.targetType === 'all'
                      ? 'border-cubus-lime bg-cubus-lime/10 text-white'
                      : 'border-white/[0.08] glass-surface text-white/70 hover:border-white/[0.1]'
                  }`}
                >
                  <Users className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Todo el Curso</div>
                    <div className="text-xs text-muted">Enviar a todos los estudiantes del curso</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, targetType: 'individual', selectedStudents: [] })}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                    formData.targetType === 'individual'
                      ? 'border-cubus-lime bg-cubus-lime/10 text-white'
                      : 'border-white/[0.08] glass-surface text-white/70 hover:border-white/[0.1]'
                  }`}
                  disabled={!formData.courseId}
                >
                  <User className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Estudiante Individual</div>
                    <div className="text-xs text-muted">Enviar a un estudiante específico</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, targetType: 'group', selectedStudents: [] })}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                    formData.targetType === 'group'
                      ? 'border-cubus-lime bg-cubus-lime/10 text-white'
                      : 'border-white/[0.08] glass-surface text-white/70 hover:border-white/[0.1]'
                  }`}
                  disabled={!formData.courseId}
                >
                  <Users className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Grupo Personalizado</div>
                    <div className="text-xs text-muted">Seleccionar varios estudiantes</div>
                  </div>
                </button>
              </div>
            </div>

            {(formData.targetType === 'individual' || formData.targetType === 'group') && formData.courseId && (
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Seleccionar Estudiantes
                </label>
                <div className="max-h-60 overflow-y-auto glass-surface rounded-lg border border-white/[0.08] p-3 space-y-2">
                  {students.length === 0 ? (
                    <p className="text-muted text-sm text-center py-4">
                      No hay estudiantes inscritos en este curso
                    </p>
                  ) : (
                    students.map((student) => (
                      <label
                        key={student.id}
                        className="flex items-center gap-3 p-2 hover:bg-white/[0.04] rounded-lg cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData.selectedStudents.includes(student.id)}
                          onChange={() => handleStudentToggle(student.id)}
                          className="w-4 h-4 rounded border-white/[0.1] text-cubus-lime focus:ring-cubus-lime focus:ring-offset-0 glass-deep"
                        />
                        <div className="flex-1">
                          <div className="text-white text-sm font-medium">{student.full_name}</div>
                          <div className="text-muted text-xs">{student.email}</div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
                {formData.selectedStudents.length > 0 && (
                  <p className="text-sm text-cubus-lime mt-2">
                    {formData.selectedStudents.length} estudiante(s) seleccionado(s)
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t border-white/[0.06]">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all font-medium"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg transition-all font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/[0.08]"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enviar Tarea
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
